import { createRef, useContext, useEffect, useState } from 'react';
import { HelpPanelContainer, HelpPanelContent } from './styles';
import { CheckCircle, Play, Question, XCircle } from '@phosphor-icons/react';
import { UsageQuota } from './UsageQuota';
import {
  TutorialObject,
  TutorialsContext,
} from '../../contexts/TutorialsContext';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  FluentProvider,
  Switch,
} from '@fluentui/react-components';
import { callFunction } from '../../lib/Helpers';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { Spinner } from '@fluentui/react';
import { Globe } from '@phosphor-icons/react';
import { TeamsFxContext } from '../../Context';
import { SendStatus } from '../../settingsPages/Help';
import { useTranslation } from 'react-i18next';
import { getTheme } from '../../../styles/themes';

interface HelpPanelProps {
  appId: string;
  firstRunEnabled?: boolean;
  isStandalone?: boolean;
}

export const HelpPanel = ({ appId, firstRunEnabled, isStandalone }: HelpPanelProps) => {
  const appInsights = useAppInsightsContext();

  const { t } = useTranslation('helpPanel');

  const { themeString, teamsUserCredential, locale } =
    useContext(TeamsFxContext);

  const [isFirstRunEnabled, setIsFirstRunEnabled] = useState(firstRunEnabled);
  const [tutorialsAvailable, setTutorialsAvailable] = useState<
    TutorialObject[]
  >([]);

  const [isVisible, setIsVisible] = useState<boolean>(false);

  const [faqItems, setFaqItems] = useState<any[]>([]);

  const [sendStatus, setSendStatus] = useState<SendStatus>(SendStatus.None);
  const refSelect = createRef<HTMLSelectElement>();
  const refText = createRef<HTMLTextAreaElement>();

  const { runTutorial, getScopeTutorials, switchFirstRun } =
    useContext(TutorialsContext);

  const sendFeedback = (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    setSendStatus(SendStatus.Loading);
    if (refText.current && refText.current.value) {
      if (refText.current.value.length > 0) {
        callFunction(
          true,
          'sendFeedback?{0}&message=' +
            refText.current.value +
            '&type=' +
            refSelect.current?.selectedOptions.item(0)?.value,
          undefined,
          false,
          teamsUserCredential
        )
          .then(() => {
            setSendStatus(SendStatus.Sent);
            if (refSelect.current) refSelect.current.selectedIndex = 0;
          })
          .catch((error) => {
            if (process.env.REACT_APP_APP_INSIGHTS !== '')
              appInsights.trackException({
                exception: error,
                properties: { component: 'Help', location: 'onSendFeedback' },
              });
            setSendStatus(SendStatus.Failed);
          });
        refText.current.value = '';
      }
    }
  };

  const onTextChange = () => {
    setSendStatus(SendStatus.None);
  };

  const renderStatus = () => {
    switch (sendStatus) {
      case SendStatus.None:
        return <></>;
      case SendStatus.Loading:
        return <Spinner size={1} />;
      case SendStatus.Sent:
        return (
          <span id="messagesent" className="statusMessage">
            <CheckCircle weight="duotone" />
            Thank you for your message.
          </span>
        );
      case SendStatus.Failed:
        return (
          <span id="messagefailed" className="statusMessage">
            <XCircle weight="duotone" />
            Please try again later.
          </span>
        );
      default:
        return <></>;
    }
  };

  useEffect(() => {
    callFunction(false, 'getHelpContent?{0}&appId=' + appId)
      .then((response) => {
        if(!isStandalone){
          const filteredResponses = response.filter((item : any) => item.group === 'default')
          setFaqItems(filteredResponses);
        }
        else setFaqItems(response);
      })
      .catch((error) => {
        if (process.env.REACT_APP_APP_INSIGHTS !== '')
          appInsights.trackException({
            exception: error,
            properties: { component: 'Help', location: 'onGetFaqItems' },
          });
      });
  }, [appId]);

  useEffect(() => {
    const tutorials = getScopeTutorials(appId);
    setTutorialsAvailable(tutorials);
  }, [appId, getScopeTutorials]);

  function handleSwitchChange() {
    setIsFirstRunEnabled(!isFirstRunEnabled);
    teamsUserCredential && switchFirstRun(appId, teamsUserCredential);
  }

  return (
    <HelpPanelContainer>
      <Question
        size={32}
        weight="fill"
        onClick={() => setIsVisible(!isVisible)}
      />
      <HelpPanelContent isVisible={isVisible}>
        <div className="helpPanelMenu">
          <Accordion defaultOpenItems="1">
            {/* <AccordionItem value="1">
              <AccordionHeader>{t('USAGE_QUOTA')}</AccordionHeader>
              <AccordionPanel>
                <UsageQuota />
              </AccordionPanel>
            </AccordionItem> */}
            <AccordionItem value="1">
              <AccordionHeader>{t('TUTORIALS')}</AccordionHeader>
              <AccordionPanel>
                <ul className="tutorials">
                  {tutorialsAvailable.map((tutorial) => (
                    <li key={'tutorial-' + tutorial.code}>
                      <button onClick={() => runTutorial(tutorial.code)}>
                        <Play weight="fill" />
                        {tutorial.title}
                      </button>
                      <FluentProvider
                        style={{
                          backgroundColor:
                            getTheme(themeString).base.neutralLighter,
                        }}
                      >
                        <Switch
                          checked={isFirstRunEnabled}
                          onChange={() => handleSwitchChange()}
                          label="Run on every launch"
                          labelPosition="before"
                        />
                      </FluentProvider>
                    </li>
                  ))}
                </ul>
              </AccordionPanel>
            </AccordionItem>
            <AccordionItem value="2">
              <AccordionHeader>{t('FAQ')}</AccordionHeader>
              <AccordionPanel>
                <div className="faq">
                  <Accordion>
                    {faqItems?.length > 0 ? (
                      faqItems.map((item: any, index) => {
                        const doctheme =
                          item?.contentUrl +
                          '?theme=' +
                          (themeString === 'default' ? 'black' : 'white');

                        return (
                          <AccordionItem value={`3.${index + 1}`}>
                            <AccordionHeader>{item.title}</AccordionHeader>
                            <AccordionPanel>
                              <div className="itemContent">
                                <p className="itemDescription">
                                  {item.description}
                                </p>
                                {item.type === 'doc' && (
                                  <a
                                    href={item.contentUrl}
                                    className="contentLink"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <Globe />
                                  </a>
                                )}
                                {item.type === 'video' && (
                                  <div className="videoContainer">
                                    <div className="iframeWrapper">
                                      <iframe
                                        title="BitFractal Readiness Video"
                                        src={item.contentUrl}
                                        allow={'autoplay'}
                                        allowFullScreen
                                      />
                                    </div>
                                  </div>
                                )}
                                {item.type === 'doc' && (
                                  <iframe
                                    src={doctheme}
                                    title={item.title}
                                  ></iframe>
                                )}
                              </div>
                            </AccordionPanel>
                          </AccordionItem>
                        );
                      })
                    ) : (
                      <span>{t('NO_ITEMS')}</span>
                    )}
                  </Accordion>
                </div>
              </AccordionPanel>
            </AccordionItem>
            <AccordionItem value="3">
              <AccordionHeader>{t('SUPPORT_AND_FEEDBACK')}</AccordionHeader>
              <AccordionPanel>
                <div className="feedback">
                  <select
                    ref={refSelect}
                    id="feedbacktype"
                    className="ideaoptions"
                    name="list"
                  >
                    <option className="ideaoptions">
                      {t('FEEDBACK_OPTION')}
                    </option>
                    <option className="ideaoptions">{t('IDEA_OPTION')}</option>
                  </select>
                  <form onSubmit={(ev) => sendFeedback(ev)}>
                    <textarea
                      name="feedback"
                      id="sendfeedback"
                      onChange={onTextChange}
                      ref={refText}
                    />
                    <div className="feedbackFooter">
                      <button type="submit">{t('SEND_BUTTON')}</button>
                      {renderStatus()}
                    </div>
                  </form>
                </div>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </div>
      </HelpPanelContent>
    </HelpPanelContainer>
  );
};
